/**
 * *Share view* — publish a board view read-only to the open web
 * (`specs/M4-V1-views.md` §2.4).
 *
 * Measured: a **670 × 655** dialog (radius 8, shadow `0 25px 50px -15px rgba(0,0,0,.7)`) with a
 * state line carrying *Stop* and *Regenerate*, a **Shareable link** row and an **Embed code**
 * row (each with *Preview* and *Copy*), and monday's three-point "Keep in mind" note
 * `[cap:sandbox-share-view.json]`. This is **not** a form's public link (V8): it is a read-only
 * render of the view itself.
 */
import { Button, useOverlayDismiss } from '@elizmonday/ui';
import { useRef, useState } from 'react';

export interface ShareViewState {
  published: boolean;
  url: string | null;
  embedUrl: string | null;
  embedCode: string | null;
}

/** monday's own wording, verbatim from the capture. */
export const KEEP_IN_MIND = [
  'Filtered data (items and columns) can still be accessed by tech-savvy users',
  'Files, Mirror, and Connect boards columns are not included in shared views',
  'Item with active permissions will not be displayed',
] as const;

function CopyRow({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="ez-share-view__row">
      <div className="ez-share-view__row-head">
        <span className="ez-share-view__row-label">{label}</span>
        <a
          className="ez-share-view__preview"
          href={value.startsWith('<') ? '#' : value}
          target="_blank"
          rel="noreferrer"
        >
          Preview
        </a>
      </div>
      <div className="ez-share-view__row-body">
        <input readOnly value={value} data-testid={`${testId}-value`} aria-label={label} />
        <Button
          kind="tertiary"
          size="small"
          data-testid={`${testId}-copy`}
          onClick={() => {
            void navigator.clipboard?.writeText(value).catch(() => undefined);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

export function ShareViewDialog({
  name,
  state,
  onPublish,
  onRegenerate,
  onStop,
  onClose,
}: {
  name: string;
  state: ShareViewState;
  onPublish(): void;
  onRegenerate(): void;
  onStop(): void;
  onClose(): void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /** **P-260.** Escape *and* the return of focus to the tab menu's trigger, from the one hook. */
  useOverlayDismiss({ onClose });

  return (
    <div className="ez-view-modal__scrim" data-testid="share-view-scrim" onClick={onClose}>
      <div
        ref={ref}
        className="ez-share-view"
        role="dialog"
        aria-modal="true"
        aria-label={`Share ${name}`}
        data-testid="share-view-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="ez-share-view__title">{name}</h2>
        <p className="ez-share-view__subtitle">Add description</p>

        {state.published ? (
          <>
            <div className="ez-share-view__state">
              <span className="ez-share-view__dot" aria-hidden="true" />
              <p>
                This board is now visible to anyone on the web with this link. You may want to
                check that it does not contain any personal information.
              </p>
              <Button kind="tertiary" onClick={onStop} data-testid="share-view-stop">
                Stop
              </Button>
              <Button kind="tertiary" onClick={onRegenerate} data-testid="share-view-regenerate">
                Regenerate
              </Button>
            </div>
            <CopyRow label="Shareable link" value={state.url ?? ''} testId="share-view-link" />
            <CopyRow label="Embed code" value={state.embedCode ?? ''} testId="share-view-embed" />
          </>
        ) : (
          <div className="ez-share-view__state">
            <p>This view is private. Publishing it gives anyone with the link read-only access.</p>
            <Button onClick={onPublish} data-testid="share-view-publish">
              Create a shareable link
            </Button>
          </div>
        )}

        <div className="ez-share-view__note">
          <strong>Keep in mind:</strong>
          <ol>
            {KEEP_IN_MIND.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
